/**
 * business.json — the single input file of the tenant pipeline.
 * The database is the runtime source of truth; this schema validates what
 * `tenant:validate` / `tenant:publish` accept. Pure module: no Node/Deno APIs,
 * so it is shared by scripts, Edge Functions tests and the UI.
 */
import { z } from 'zod';

export const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;
export const KEY_RE = /^[a-z0-9][a-z0-9_-]{0,47}$/;
const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$|^24:00$/;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const PHONE_RE = /^\+[1-9]\d{7,14}$/;
const IMAGE_RE = /^images\/[a-zA-Z0-9._/-]+\.(jpe?g|png|webp)$/;

export const RESERVED_SLUGS = new Set(['owner', 'assets', 'api', 'admin', 's', 'sw', 'icons', 'static']);

export function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

const interval = z
  .object({ opens: z.string().regex(HHMM_RE), closes: z.string().regex(HHMM_RE) })
  .refine((v) => toMinutes(v.closes) > toMinutes(v.opens), { message: 'closes must be after opens' });

const weekday = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
export type Weekday = z.infer<typeof weekday>;
export const WEEKDAYS: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const businessSchema = z
  .object({
    $schema: z.string().optional(),
    slug: z.string().regex(SLUG_RE, 'slug: латиница, цифры и дефис').refine((s) => !RESERVED_SLUGS.has(s), 'slug зарезервирован'),
    demo: z.boolean().default(true),
    locale: z.literal('ru-RU').default('ru-RU'),
    timezone: z.string().refine(isValidTimezone, 'неизвестный IANA timezone'),
    currency: z.enum(['RUB', 'KZT', 'BYN', 'USD', 'EUR']).default('RUB'),
    brand: z.object({
      name: z.string().min(2).max(60),
      shortName: z.string().min(2).max(12),
      tagline: z.string().min(2).max(90),
      description: z.string().min(10).max(600),
      accentColor: z.string().regex(HEX_RE),
      logo: z.string().regex(IMAGE_RE),
      icon: z.string().regex(IMAGE_RE),
      hero: z.string().regex(IMAGE_RE),
      heroAlt: z.string().min(3).max(160),
    }),
    contacts: z.object({
      phone: z.string().regex(PHONE_RE, 'телефон в формате E.164, например +79991234567'),
      address: z.string().min(5).max(200),
      mapUrl: z.string().url().optional(),
      telegram: z.string().max(64).optional(),
      howToFind: z.string().max(400).optional(),
    }),
    booking: z.object({
      slotStepMinutes: z.number().int().min(10).max(240).default(30),
      leadTimeMinutes: z.number().int().min(0).max(7 * 24 * 60).default(60),
      horizonDays: z.number().int().min(1).max(180).default(30),
      cancelMinHoursBefore: z.number().int().min(0).max(168).default(12),
      reminderHoursBefore: z.number().int().min(1).max(72).default(24),
    }),
    resources: z
      .array(
        z.object({
          key: z.string().regex(KEY_RE),
          name: z.string().min(1).max(40),
        }),
      )
      .min(1)
      .max(30),
    services: z
      .array(
        z.object({
          key: z.string().regex(KEY_RE),
          name: z.string().min(2).max(80),
          description: z.string().max(400).default(''),
          category: z.string().max(40).default(''),
          durationMinutes: z.number().int().min(10).max(14 * 24 * 60),
          bufferMinutes: z.number().int().min(0).max(24 * 60).default(0),
          price: z.number().int().min(0).max(100_000_000),
          priceIsFrom: z.boolean().default(false),
          resources: z.array(z.string().regex(KEY_RE)).min(1),
          image: z.string().regex(IMAGE_RE).optional(),
        }),
      )
      .min(1)
      .max(60),
    hours: z.record(weekday, z.array(interval).max(3)),
    closedDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).default([]),
    infoCards: z
      .array(z.object({ title: z.string().min(2).max(40), text: z.string().min(2).max(160), icon: z.enum(['shield', 'clock', 'sparkle', 'car', 'drop', 'wrench', 'star', 'medal']) }))
      .length(3),
    gallery: z
      .array(z.object({ key: z.string().regex(KEY_RE), image: z.string().regex(IMAGE_RE), caption: z.string().min(1).max(120) }))
      .max(40)
      .default([]),
    owner: z.object({ email: z.string().email(), name: z.string().min(1).max(80).optional() }),
    assistant: z
      .object({
        enabled: z.boolean().default(true),
        examples: z.array(z.string().min(3).max(60)).min(1).max(5).optional(),
      })
      .default({ enabled: true }),
  })
  .superRefine((cfg, ctx) => {
    const resourceKeys = new Set<string>();
    cfg.resources.forEach((r, i) => {
      if (resourceKeys.has(r.key)) ctx.addIssue({ code: 'custom', path: ['resources', i, 'key'], message: `дубликат ресурса ${r.key}` });
      resourceKeys.add(r.key);
    });
    const serviceKeys = new Set<string>();
    cfg.services.forEach((s, i) => {
      if (serviceKeys.has(s.key)) ctx.addIssue({ code: 'custom', path: ['services', i, 'key'], message: `дубликат услуги ${s.key}` });
      serviceKeys.add(s.key);
      s.resources.forEach((rk, j) => {
        if (!resourceKeys.has(rk)) ctx.addIssue({ code: 'custom', path: ['services', i, 'resources', j], message: `нет ресурса ${rk}` });
      });
    });
    const galleryKeys = new Set<string>();
    cfg.gallery.forEach((g, i) => {
      if (galleryKeys.has(g.key)) ctx.addIssue({ code: 'custom', path: ['gallery', i, 'key'], message: `дубликат фото ${g.key}` });
      galleryKeys.add(g.key);
    });
    const openDays = WEEKDAYS.filter((d) => (cfg.hours[d] ?? []).length > 0);
    if (openDays.length === 0) ctx.addIssue({ code: 'custom', path: ['hours'], message: 'нет ни одного рабочего дня' });
    for (const d of WEEKDAYS) {
      const list = [...(cfg.hours[d] ?? [])].sort((a, b) => toMinutes(a.opens) - toMinutes(b.opens));
      for (let i = 1; i < list.length; i++) {
        if (toMinutes(list[i]!.opens) < toMinutes(list[i - 1]!.closes)) {
          ctx.addIssue({ code: 'custom', path: ['hours', d], message: 'интервалы пересекаются' });
        }
      }
    }
  });

export type BusinessConfig = z.output<typeof businessSchema>;
export type BusinessConfigInput = z.input<typeof businessSchema>;

/** Every image path referenced by the config (relative to tenants/<slug>/). */
export function referencedImages(cfg: BusinessConfig): string[] {
  const set = new Set<string>([cfg.brand.logo, cfg.brand.icon, cfg.brand.hero]);
  cfg.services.forEach((s) => s.image && set.add(s.image));
  cfg.gallery.forEach((g) => set.add(g.image));
  return [...set];
}

/** WCAG relative luminance contrast between two hex colors. */
export function contrastRatio(a: string, b: string): number {
  const lum = (hex: string) => {
    const n = parseInt(hex.slice(1), 16);
    const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
      const c = v / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * ch[0]! + 0.7152 * ch[1]! + 0.0722 * ch[2]!;
  };
  const [l1, l2] = [lum(a), lum(b)].sort((x, y) => y - x);
  return (l1! + 0.05) / (l2! + 0.05);
}

/** Text color to place on the accent: whichever of black/white contrasts more. */
export function onAccent(accent: string): '#000000' | '#FFFFFF' {
  return contrastRatio(accent, '#000000') >= contrastRatio(accent, '#FFFFFF') ? '#000000' : '#FFFFFF';
}
